import { useCallback, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import type {
  CsvQuestionInputItem,
  DifyAPITestFormValues,
  DifySubmitPreparationResult,
  InputMethod,
} from "~/components/difyAPITest/difyAPITest.types";
import { useQuestionChipInput } from "~/components/difyAPITest/hooks/useQuestionChipInput";
import { readCsvQuestionInputFile } from "~/services/difyAPITest/difyAPITestCsvService";

/**
 * DifyAPIテスト画面の入力フォーム状態を管理するhook
 * @return フォーム状態と入力操作関数
 */
export const useDifyAPITestForm = () => {
  const [formValues, setFormValues] = useState<DifyAPITestFormValues>({
    inputMethod: "text",
    draft: "",
    questions: [],
    file: null,
  });
  const [csvQuestions, setCsvQuestions] = useState<CsvQuestionInputItem[]>([]); // CSVから読み込んだ質問入力項目
  const [csvValidationMessage, setCsvValidationMessage] = useState<string | null>(null); // CSV入力のバリデーションエラーメッセージ
  const [isParsingCsv, setIsParsingCsv] = useState(false); // CSV解析中フラグ
  const csvParseRequestIdRef = useRef(0); // CSV解析リクエストID
  const { inputMethod, draft, questions: questionList, file: selectedFile } = formValues;

  /**
   * 入力方法を更新する
   * @param nextInputMethod 次の入力方法
   * @return 更新処理の完了
   */
  const setInputMethod = useCallback((nextInputMethod: InputMethod) => {
    setFormValues((prev) => ({ ...prev, inputMethod: nextInputMethod }));
  }, []);

  /**
   * テキスト入力の下書きを更新する
   * @param nextDraft 次の下書き文字列
   * @return 更新処理の完了
   */
  const setDraft = useCallback((nextDraft: string) => {
    setFormValues((prev) => ({ ...prev, draft: nextDraft }));
  }, []);

  /**
   * 質問一覧を更新する
   * @param nextQuestions 次の質問一覧
   * @return 更新処理の完了
   */
  const setQuestions = useCallback((nextQuestions: string[]) => {
    setFormValues((prev) => ({ ...prev, questions: nextQuestions }));
  }, []);

  /**
   * ファイル入力を更新してCSVを解析する
   * @param event ファイル選択イベント
   * @return 更新処理の完了
   */
  const handleFileChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    const nextRequestId = csvParseRequestIdRef.current + 1;

    csvParseRequestIdRef.current = nextRequestId;

    setFormValues((prev) => ({ ...prev, file: nextFile }));

    if (!nextFile) {
      setCsvQuestions([]);
      setCsvValidationMessage(null);
      setIsParsingCsv(false);
      return;
    }

    setCsvQuestions([]);
    setCsvValidationMessage(null);
    setIsParsingCsv(true);

    try {
      // CSVファイルから質問入力一覧を読み込む
      const nextQuestions = await readCsvQuestionInputFile(nextFile);

      // 途中で入力ファイルの変更があった場合は結果を反映しない
      if (csvParseRequestIdRef.current !== nextRequestId) {
        return;
      }

      setCsvQuestions(nextQuestions);
    } catch (error: unknown) {
      // 途中で入力ファイルの変更があった場合は結果を反映しない
      if (csvParseRequestIdRef.current !== nextRequestId) {
        return;
      }

      setCsvQuestions([]);
      setCsvValidationMessage(
        error instanceof Error ? error.message : "CSVファイルの読み込みに失敗しました。"
      );
    } finally {
      // 途中で入力ファイルの変更がなかった場合のみ解析中フラグを下ろす
      if (csvParseRequestIdRef.current === nextRequestId) {
        setIsParsingCsv(false);
      }
    }
  }, []);

  /**
   * 現在の入力内容をクリアする
   * @return クリア処理の完了
   */
  const clearCurrentInput = useCallback(() => {
    setFormValues((prev) => {
      if (prev.inputMethod === "text") {
        return {
          ...prev,
          draft: "",
          questions: [],
        };
      }

      return {
        ...prev,
        file: null,
      };
    });

    csvParseRequestIdRef.current += 1;
    setCsvQuestions([]);
    setCsvValidationMessage(null);
    setIsParsingCsv(false);
  }, []);

  // テキスト入力は複雑な為、質問チップ入力のロジックを管理するカスタムフックを利用する
  const questionChipInput = useQuestionChipInput({
    draft,
    questionList,
    setDraft,
    setQuestions,
  });

  /**
   * API送信のためのペイロードを構築する
   * 入力方法に応じてテキスト質問リストまたはファイルをペイロードに含める
   * @return 送信ペイロードとバリデーション結果
   */
  const buildSubmitPayload = useCallback((): DifySubmitPreparationResult => {
    // 入力方法：テキスト
    if (inputMethod === "text") {
      const questions = questionList;

      if (questions.length === 0) {
        return {
          payload: null,
          validationMessage: "送信する質問を指定してください。",
        };
      }

      return {
        payload: {
          inputMethod: "text",
          questions: questions.map((question) => ({ question })),
        },
        validationMessage: null,
      };
    }

    // 入力方法：ファイル
    if (inputMethod === "file") {
      const { file } = formValues;

      if (!file) {
        return {
          payload: null,
          validationMessage: "送信するCSVファイルを選択してください。",
        };
      }

      if (isParsingCsv) {
        return {
          payload: null,
          validationMessage: "CSVファイルを解析中です。",
        };
      }

      if (csvValidationMessage) {
        return {
          payload: null,
          validationMessage: csvValidationMessage,
        };
      }

      if (csvQuestions.length === 0) {
        return {
          payload: null,
          validationMessage: "CSVファイルに有効なデータがありません。",
        };
      }

      return {
        payload: {
          inputMethod: "file",
          questions: csvQuestions,
        },
        validationMessage: null,
      };
    }

    return {
      payload: null,
      validationMessage: "入力方法が不正です。",
    };
  }, [inputMethod, questionList, formValues, isParsingCsv, csvValidationMessage, csvQuestions]);

  return {
    formValues,
    inputMethod,
    draft,
    questionList,
    selectedFile,
    setInputMethod,
    setDraft,
    setQuestions,
    handleFileChange,
    clearCurrentInput,
    buildSubmitPayload,
    ...questionChipInput,
  };
};
